'use client';

import type { QuestionReconstructedType } from '~/model/QuestionType';
import type { ResultsQuestionsValueType } from '~/model/ResultsType';
import { useSearchParams } from 'next/navigation';
import { useLayoutEffect, useState } from 'react';
import { getResizedPictureUrl } from '@/app/utils/entities/media';
import { ResizedImg } from '~/components/commons/ResizedImg/ResizedImg';
import { Zoom } from '~/components/commons/Zoom/Zoom';
import { api, apiTypes } from '~/const/api';
import { scrollToElement } from '~/utils/utils';
import { Comments } from '../Comments/Comments';
import { getCssClassFromType, hasChoicesType } from '../utils';
import { ChoicesJarResult } from './ChoicesJarResult';
import { ChoicesResult } from './ChoicesResult';
import { GridResult } from './GridResult';
import { RankingPictureResult } from './RankingPictureResult';
import { RankingResult } from './RankingResult';
import { RankingScaleResult } from './RankingScaleResult';
import { ScaleResult } from './ScaleResult';
import { SelectResult } from './SelectResult';
import { StarRatingResult } from './StarRatingResult';
import { VASResult } from './VASResult';

export const Result = ({ question, result }: ResultPropsType) => {
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
  const searchParams = useSearchParams();

  const questionType = question?.type;
  const questionMedia = question?.media;
  const hasChoices: boolean = hasChoicesType(questionType);
  const cssType: string = getCssClassFromType(questionType);
  const cssTypeClass = cssType ? `results__question--${cssType}` : '';
  const resultId = result?.result_id;
  const resultData = result?.data;

  useLayoutEffect(() => {
    if (
      !hasAutoScrolled
      && searchParams.get('q')
      && result[`/api/${api.QUESTIONS}/${searchParams.get('q')}`]
    ) {
      const anchor = `q-${searchParams.get('q')}`;
      setTimeout(() => scrollToElement(anchor), 500);
      setHasAutoScrolled(true);
    }
  }, [hasAutoScrolled, result, searchParams]);

  return (
    <article
      data-testid="result"
      className={`panel results__question ${
        hasChoices ? 'results__question--choices' : ''
      } ${cssTypeClass}`}
    >
      <header className="panel__header">
        <div className="results__title vp-title-s">{question?.title}</div>
      </header>
      <footer className="panel__body">
        {questionMedia && (
          <div
            className="results__question-picture"
            data-testid="question-picture"
          >
            <div className="results__question-picture-inner">
              <ResizedImg
                src={questionMedia.content_url}
                width={720}
                height={405}
                format="720x0"
                alt=""
              />
              <Zoom
                mainSrc={getResizedPictureUrl(
                  questionMedia.content_url,
                  '1600x0',
                )}
              />
            </div>
          </div>
        )}
        {(questionType === apiTypes.VAS1 || questionType === apiTypes.VAS2) && (
          <VASResult
            average={resultData.average}
            limitMin={question.limit_min}
            limitMax={question.limit_max}
            type={questionType}
          />
        )}
        {questionType === apiTypes.CHOICES_JAR && (
          <ChoicesJarResult
            resultId={resultId}
            resultData={resultData}
            answerChoices={question?.choices}
          />
        )}
        {questionType === apiTypes.STAR_RATING && (
          <StarRatingResult
            resultId={resultId}
            resultData={resultData}
            subQuestions={question?.sub_questions}
          />
        )}
        {questionType === apiTypes.RANKING && (
          <RankingResult
            resultId={resultId}
            resultData={resultData}
            answerChoices={question?.choices}
          />
        )}
        {questionType === apiTypes.RANKING_PICTURE && (
          <RankingPictureResult
            result={result}
            answerChoices={question?.choices}
          />
        )}
        {(questionType === apiTypes.RADIO
          || questionType === apiTypes.CHECK
          || questionType === apiTypes.CONDITIONAL) && (
          <ChoicesResult
            resultId={resultId}
            resultData={resultData}
            type={questionType}
            answerChoices={question?.choices}
          />
        )}
        {questionType === apiTypes.GRID && (
          <GridResult
            resultId={resultId}
            resultData={resultData}
            answerChoices={question?.choices}
            subQuestions={question?.sub_questions}
          />
        )}
        {questionType === apiTypes.SELECT && (
          <SelectResult
            resultId={resultId}
            resultData={resultData}
            answerChoices={question?.choices}
          />
        )}
        {questionType === apiTypes.SCALE && (
          <ScaleResult
            question={question}
            resultData={resultData}
            questionLabel={question}
          />
        )}
        {questionType === apiTypes.RANKING_SCALE && (
          <RankingScaleResult
            resultId={resultId}
            resultData={resultData}
            answerChoices={question?.choices}
            question={question}
          />
        )}
        <Comments resultId={resultId} />
      </footer>
    </article>
  );
};

export interface ResultPropsType {
  question: QuestionReconstructedType;
  result: ResultsQuestionsValueType<any>;
}
